import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Paper, Box } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { GridToolbar } from '@mui/x-data-grid-premium';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from '../../state/slices/snackBarSlice';
import { setTradePartnersData } from '../../state/slices/tradePartnersSlice';
import { setAddOnsData } from '../../state/slices/addOnsSlice';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { callAPI } from '../../api/useApiRequest';
import CustomSkeleton2 from '../../components/Skeleton';
import CustomSnackbar from '../../components/CustomSnackbar';
import CircularLoading from '../../components/CircularLoading';

import { acctCol } from './TableDef';
import AccountDialog from './AccountDialog';

export default function AccountSetting() {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Retrieve authentication details from Auth0. */
    const { user, getAccessTokenSilently } = useAuth0();

    const tradePartnerList = useSelector((state) => state.tradePartners);

    /** State for registered account info. */
    const [acctRows, setAcctRows] = useState([]);
    /** State for managing Dialog. */
    const [accountDialogOpen, setAccountDialogOpen] = useState(false);
    /** State for a selected row Id value. */
    const [selectedId, setSelectedId] = useState('');
    const [loading, setLoading] = useState({ get: false, delete: false });

    useEffect(() => {
        document.title = 'Account Setting | Management';

        const fetchAllData = async () => {
            try {
                setLoading({ ...loading, get: true });
                // API calls in parallel.
                const [customersResponse, tradePartnersResponse, addOnsResponse] = await Promise.all([
                    callAPI({
                        method: 'get',
                        module: 'customers',
                        component: 'customer_trade_partners, customer_add_ons',
                        getAccessTokenSilently,
                        email: user.email,
                        onSuccess: (resp) => {
                            setAcctRows(resp.data);
                        },
                    }),
                    callAPI({
                        method: 'get',
                        module: 'tradepartners',
                        component: null,
                        getAccessTokenSilently,
                        email: user.email,
                        onSuccess: (resp) => {
                            dispatch(setTradePartnersData(resp.data));
                        },
                    }),
                    callAPI({
                        method: 'get',
                        module: 'addons',
                        component: null,
                        getAccessTokenSilently,
                        email: user.email,
                        onSuccess: (resp) => {
                            dispatch(setAddOnsData(resp.data));
                        },
                    }),
                ]);
            } catch (error) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Something went wrong. Please try again.',
                        severity: 'error',
                    }),
                );
            } finally {
                setLoading({ ...loading, get: false });
            }
        };
        fetchAllData();
    }, []);

    /** Handle to open the account dialog. */
    const handleClickOpen = (event, id) => {
        event.preventDefault();
        setAccountDialogOpen(true);
        setSelectedId(id);
    };

    /** Handle to delete an account. */
    const handleClickDelete = (event, id) => {
        if (window.confirm('Are you sure you want to delete this account?')) {
            event.preventDefault();
            callAPI({
                method: 'delete',
                module: 'customers',
                component: null,
                getAccessTokenSilently,
                email: user.email,
                obj: JSON.stringify({ id: id }),
                onInit: () => {
                    setLoading({ ...loading, delete: true });
                },
                onSuccess: (resp) => {
                    setAcctRows(acctRows.filter((item) => item.id !== id));
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Account has been successfully deleted.',
                            severity: 'success',
                        }),
                    );
                },
                onError: () => {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Something went wrong. Please try again.',
                            severity: 'error',
                        }),
                    );
                },
                onFinal: () => {
                    setLoading({ ...loading, delete: false });
                },
            });
        }
    };

    if (loading.delete) {
        return <CircularLoading loading={loading.delete} />;
    }

    return (
        <>
            <Box maxWidth="97%" sx={{ mt: 2, mx: 'auto', height: '90vh' }}>
                <Paper
                    sx={{
                        borderRadius: 5,
                        p: 1,
                        height: 'inherit',
                    }}
                >
                    {loading.get ? (
                        <CustomSkeleton2 />
                    ) : (
                        <DataGridPremium
                            rows={acctRows}
                            columns={acctCol(handleClickOpen, handleClickDelete, tradePartnerList)}
                            density="compact"
                            disableRowSelectionOnClick
                            unstable_cellSelection
                            disableDensitySelector
                            disablePrintSelection
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                },
                            }}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        exist: false,
                                    },
                                },
                            }}
                            sx={{ border: 'none' }}
                        />
                    )}
                </Paper>
                <AccountDialog
                    acctRows={acctRows}
                    setAcctRows={setAcctRows}
                    accountDialogOpen={accountDialogOpen}
                    setAccountDialogOpen={setAccountDialogOpen}
                    selectedId={selectedId}
                />
                <CustomSnackbar />
            </Box>
        </>
    );
}
